import { useEffect, useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { X, Camera } from "lucide-react";
import { Reveal, SectionHead } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import CtaBand from "@/components/CtaBand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Gallery = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/gallery`)
      .then((res) => setItems(res.data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <>
      <section className="bg-asphalt py-20 sm:py-28 grain relative" data-testid="gallery-page-hero">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <p className="font-mono text-xs tracking-[0.3em] text-emergency uppercase">Recent Jobs</p>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-black uppercase leading-[1.02] text-white">
            Real trucks.<br /><span className="text-emergency">Real rescues.</span>
          </h1>
          <p className="mt-6 max-w-xl text-lg text-zinc-400">
            Fresh from the field — every photo is an actual Paliway Towing job in and around Tarrant County.
          </p>
        </div>
      </section>
      <Marquee />

      <section className="bg-[#F8F9FA] py-20 sm:py-28" data-testid="gallery-grid-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <SectionHead eyebrow="The Work" title="Latest from the road" />
          {loading ? (
            <p className="font-mono text-sm text-zinc-500 uppercase tracking-widest" data-testid="gallery-loading">Loading photos...</p>
          ) : items.length === 0 ? (
            <p className="font-mono text-sm text-zinc-500 uppercase tracking-widest" data-testid="gallery-empty">No photos yet — check back soon.</p>
          ) : (
            <div className="columns-1 sm:columns-2 lg:columns-3 gap-5">
              {items.map((item, i) => (
                <Reveal key={item.id} delay={(i % 3) * 0.06} className="mb-5 break-inside-avoid">
                  <button
                    onClick={() => setSelected(item)}
                    data-testid={`gallery-photo-${i}`}
                    className="group relative block w-full overflow-hidden border border-zinc-200 bg-charcoal text-left"
                  >
                    <img
                      src={item.src}
                      alt={item.caption || "Paliway Towing job photo"}
                      loading="lazy"
                      className="w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-asphalt/85 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                    {item.caption && (
                      <p className="absolute bottom-0 left-0 right-0 translate-y-2 p-4 font-mono text-[11px] tracking-wider uppercase text-white opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100">
                        {item.caption}
                      </p>
                    )}
                    <span className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center bg-emergency text-white opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                      <Camera className="h-4 w-4" />
                    </span>
                  </button>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      <AnimatePresence>
        {selected && (
          <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="gallery-lightbox">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelected(null)}
              className="absolute inset-0 bg-black/85 backdrop-blur-sm"
            />
            <motion.figure
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.94 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              className="relative max-h-[88vh] max-w-4xl"
            >
              <img src={selected.src} alt={selected.caption || "Paliway Towing job photo"} className="max-h-[78vh] w-full object-contain" />
              {selected.caption && (
                <figcaption className="mt-3 text-center font-mono text-xs tracking-[0.2em] uppercase text-zinc-300">
                  {selected.caption}
                </figcaption>
              )}
              <button
                onClick={() => setSelected(null)}
                data-testid="gallery-lightbox-close"
                className="absolute -right-2 -top-2 flex h-10 w-10 items-center justify-center bg-emergency text-white"
                aria-label="Close photo"
              >
                <X className="h-5 w-5" />
              </button>
            </motion.figure>
          </div>
        )}
      </AnimatePresence>

      <CtaBand />
    </>
  );
};

export default Gallery;
